import type { Degree, Distance, Point, Rectangle } from "@pdfking/math"
import { Min, Scale, Total } from "@pdfking/math"
import { HALF_TURN, QUARTER_TURN, ZERO_DEGREE } from "@pdfking/math/constants"
import { MM } from "@pdfking/quantity"
import type { PathPoint } from "@pdfking/renderer-api"
import { ArcPoints, DrawPath, Oval, PlotFunction } from "@pdfking/renderer-api"

import type { GrowPlotter, PlotContribute } from "#internal"
import { CUT_LAYER_ID } from "#internal"

// The page cut along closed paths of `points`: its outline, and any hole in it.
const cut = (...paths: PathPoint[][]): PlotContribute =>
  ({
    [CUT_LAYER_ID]: paths.map(points => ({
      enum: PlotFunction.PLOT_PATH,
      points,
      stroke_color: "cut",
      closed: true,
    })),
  }) as PlotContribute

// A hang tag's string hole and how far its centre is in from the edge, as the
// shop's tags have them
const HOLE = MM(2)
const HOLE_IN = MM(10)
// How far a tag's corners are cut off on the hole's edge
const CHAMFER = MM(14)

// The hole, centred on the binding edge, the left, the edge a tag hangs from:
// on top where it opens there (see CreateExtraShapes), as its safe margin.
const hole = ({ height: h }: Rectangle<Distance>): PathPoint[] =>
  Oval({ x: HOLE_IN, y: h / 2 } as Point<Distance>, HOLE)

// Python `rounded_rect_plot_points_list`: the page cut to a rectangle whose
// corners are quarters of an ellipse of radii `rx`, `ry`, each one Bézier,
// joined by the straight sides.
const rounded_rect = ({ width: w, height: h }: Rectangle<Distance>, rx: Distance, ry: Distance) => {
  const corner = (x: number, y: number, from: Degree) =>
    ArcPoints({ x, y } as Point<Distance>, rx, from, QUARTER_TURN, ry)
  return [
    ...corner(w - rx, h - ry, ZERO_DEGREE),
    ...corner(rx, h - ry, QUARTER_TURN),
    ...corner(rx, ry, HALF_TURN),
    ...corner(w - rx, ry, Total(HALF_TURN, QUARTER_TURN)),
  ]
}

// The ellipse the page fits in
const oval = ({ width, height }: Rectangle<Distance>): PathPoint[] =>
  Oval(
    { x: width / 2, y: height / 2 } as Point<Distance>,
    { x: width / 2, y: height / 2 } as Point<Distance>
  )

// A tag: the page with the two corners of the edge it hangs from cut off
const tag = ({ width: w, height: h }: Rectangle<Distance>): PathPoint[] => {
  const c = Min(CHAMFER, Scale(Min(w, h), 0.3))
  const corners: [number, number][] = [
    [c, 0],
    [w, 0],
    [w, h],
    [c, h],
    [0, h - c],
    [0, c],
  ]
  return DrawPath(corners.map(([x, y]) => ({ x, y }) as Point<Distance>))
}

/** Python `template_grow.py` r1: the page with its corners rounded to 5 mm. */
export const PlotRoundedCorners: GrowPlotter = size => cut(rounded_rect(size, MM(5), MM(5)))

/** Python `template_grow.py` r2: the ellipse the page fits in. */
export const PlotOval: GrowPlotter = size => cut(oval(size))

/** A hang tag with its corners rounded to 5 mm, its string hole by the edge it
 * hangs from. */
export const PlotRoundedCornersHole: GrowPlotter = size =>
  cut(rounded_rect(size, MM(5), MM(5)), hole(size))

/** A round or oval hang tag, its string hole by the edge it hangs from. */
export const PlotOvalHole: GrowPlotter = size => cut(oval(size), hole(size))

/** A hang tag cut to a point at the edge it hangs from, its string hole there. */
export const PlotTagHole: GrowPlotter = size => cut(tag(size), hole(size))
